import { useAuthStore } from '@/store/authStore';

export enum Roles {
  Shared = 'Shared',
  Monitoring = 'Monitoring',
  SuperAdmin = 'SuperAdmin',
  User = 'User',
  IntegratorAdmin = 'IntegratorAdmin',
  ProjectAdmin = 'ProjectAdmin',
  SalesPerson = 'SalesPerson',
  Installer = 'Installer',
  CompanyAdmin = 'CompanyAdmin',
}

const baseRoutes: { route: string; access: Roles[] }[] = [
  // Shared routes
  { route: '/home', access: [Roles.Shared] },
  { route: '/dashboard', access: [Roles.Shared] },
  { route: '/', access: [Roles.Shared] },

  // Monitoring routes
  { route: '/monitor', access: [Roles.Monitoring] },
  {
    route: '/teams',
    access: [Roles.SuperAdmin, Roles.CompanyAdmin, Roles.ProjectAdmin, Roles.IntegratorAdmin],
  },
  {
    route: '/monitoring-overview',
    access: [
      Roles.Monitoring,
      Roles.SuperAdmin,
      Roles.CompanyAdmin,
      Roles.ProjectAdmin,
      Roles.IntegratorAdmin,
    ],
  },
  // Super Admin routes
  { route: '/system', access: [Roles.SuperAdmin] },
  { route: '/ingestion', access: [Roles.SuperAdmin] },
  { route: '/ingestion/legacy', access: [Roles.SuperAdmin] },

  { route: '/reporting', access: [Roles.SuperAdmin, Roles.IntegratorAdmin] },
  { route: '/billing', access: [Roles.SuperAdmin, Roles.IntegratorAdmin] },

  // Admin routes
  {
    route: '/team',
    access: [Roles.IntegratorAdmin, Roles.SuperAdmin],
  },
  {
    route: '/viewer',
    access: [Roles.IntegratorAdmin],
  },
  {
    route: '/devices',
    access: [Roles.ProjectAdmin, Roles.IntegratorAdmin, Roles.CompanyAdmin, Roles.SuperAdmin],
  },
  {
    route: '/sites',
    access: [Roles.ProjectAdmin, Roles.IntegratorAdmin, Roles.CompanyAdmin, Roles.SuperAdmin],
  },
  {
    route: '/alerts',
    access: [Roles.ProjectAdmin, Roles.IntegratorAdmin, Roles.CompanyAdmin, Roles.SuperAdmin],
  },
  {
    route: '/customers',
    access: [Roles.ProjectAdmin, Roles.IntegratorAdmin, Roles.CompanyAdmin, Roles.SuperAdmin],
  },
  {
    route: '/connections',
    access: [Roles.ProjectAdmin, Roles.IntegratorAdmin, Roles.CompanyAdmin, Roles.SuperAdmin],
  },
  {
    route: '/edgelink',
    access: [Roles.IntegratorAdmin]
  },
  {
    route: '/users',
    access: [Roles.IntegratorAdmin, Roles.SuperAdmin],
  },
  {
    route: '/alert-audit',
    access: [Roles.IntegratorAdmin, Roles.SuperAdmin],
  },

  // Super Admin routes
  { route: '/ihub', access: [Roles.SuperAdmin] },
  { route: '/edgelink-deployments', access: [Roles.SuperAdmin] },
];

export const useRouteAccess = () => {
  let { roles } = useAuthStore();

  const hasAccessToRoute = (route: string) => {
    const routeAccess = baseRoutes.find((r) => r.route === route);

    if (!routeAccess) return false;

    // If route has Shared role, everyone has access
    if (routeAccess.access.includes(Roles.Shared)) return true;

    return routeAccess.access.some((role) => roles.includes(role));
  };

  const hasRoleAccess = (role: Roles) => roles.includes(role);

  return {
    hasAccessToRoute,
    hasRoleAccess,
  };
};
